import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { findMatchingWords, replaceWordText } from '../utils/searchHelpers';
import { translate } from '../../i18n';

/**
 * Custom hook for search functionality in the caption editor
 */
export const useSearch = (captionData, updateCaptionData, showToast, confirmAction) => {
  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replaceQuery, setReplaceQuery] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  
  // Search options
  const [matchCase, setMatchCase] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  
  // Debounce timer
  const debounceRef = useRef(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query (300ms)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  // Find all matches
  const matches = useMemo(() => {
    if (!debouncedQuery || !captionData) return [];
    
    return findMatchingWords(captionData, debouncedQuery, {
      matchCase,
      useRegex,
      wholeWord
    });
  }, [captionData, debouncedQuery, matchCase, useRegex, wholeWord]);

  // Set of matching word IDs for quick lookup
  const matchingWordIds = useMemo(() => {
    return new Set(matches.map(m => m.wordId));
  }, [matches]);

  // Current match
  const currentMatch = useMemo(() => {
    if (matches.length === 0) return null;
    const safeIndex = Math.min(currentMatchIndex, matches.length - 1);
    return matches[safeIndex];
  }, [matches, currentMatchIndex]);

  // Reset current match index when matches change
  useEffect(() => {
    if (matches.length > 0 && currentMatchIndex >= matches.length) {
      setCurrentMatchIndex(0);
    }
  }, [matches, currentMatchIndex]);

  // Open search
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  // Close search
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setReplaceQuery('');
    setShowReplace(false);
    setCurrentMatchIndex(0);
    setDebouncedQuery('');
  }, []);

  // Navigate to next match
  const goToNextMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matches.length);
  }, [matches.length]);

  // Navigate to previous match
  const goToPreviousMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
  }, [matches.length]);

  // Jump to specific match
  const goToMatch = useCallback((index) => {
    if (index >= 0 && index < matches.length) {
      setCurrentMatchIndex(index);
    }
  }, [matches.length]);

  // Replace current match
  const replaceCurrentMatch = useCallback(() => {
    if (!currentMatch || !replaceQuery === undefined) return;
    
    const newWords = captionData.words.map(word => {
      if (word.id === currentMatch.wordId) {
        return replaceWordText(word, debouncedQuery, replaceQuery, {
          matchCase,
          useRegex
        });
      }
      return word;
    });
    
    updateCaptionData({ ...captionData, words: newWords });
    showToast?.('Replaced 1 match', 'success');
    
    // Move to next match (if any)
    if (matches.length > 1) {
      // Stay at same index since current match is now gone
      // Matches will recalculate and the next match becomes current
    }
  }, [currentMatch, replaceQuery, debouncedQuery, captionData, updateCaptionData, matchCase, useRegex, matches.length, showToast]);

  // Replace all matches
  const replaceAllMatches = useCallback(async (skipConfirmation = false) => {
    if (matches.length === 0) return;
    
    // Confirm if more than 10 matches
    if (!skipConfirmation && matches.length > 10) {
      const message = translate('Replace all {count} matches?', { count: matches.length });
      let confirmed = true;
      if (confirmAction) {
        confirmed = await confirmAction({
          title: 'Replace all matches',
          message,
          confirmLabel: 'Replace all',
          cancelLabel: 'Cancel'
        });
      } else {
        confirmed = window.confirm(message);
      }
      if (!confirmed) return;
    }
    
    const matchIds = new Set(matches.map(m => m.wordId));
    
    const newWords = captionData.words.map(word => {
      if (matchIds.has(word.id)) {
        return replaceWordText(word, debouncedQuery, replaceQuery, {
          matchCase,
          useRegex
        });
      }
      return word;
    });
    
    updateCaptionData({ ...captionData, words: newWords });
    showToast?.(translate('Replaced {count} matches', { count: matches.length }), 'success');
    setCurrentMatchIndex(0);
  }, [matches, replaceQuery, debouncedQuery, captionData, updateCaptionData, matchCase, useRegex, showToast, confirmAction]);

  // Toggle options
  const toggleMatchCase = useCallback(() => setMatchCase(prev => !prev), []);
  const toggleUseRegex = useCallback(() => setUseRegex(prev => !prev), []);
  const toggleWholeWord = useCallback(() => setWholeWord(prev => !prev), []);
  const toggleShowReplace = useCallback(() => setShowReplace(prev => !prev), []);

  // Keyboard shortcut handler
  const handleKeyDown = useCallback((e) => {
    if (!isSearchOpen) return;
    
    // Enter - go to next match
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      goToNextMatch();
    }
    // Shift+Enter - go to previous match
    else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      goToPreviousMatch();
    }
    // Escape - close search
    else if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
    }
  }, [isSearchOpen, goToNextMatch, goToPreviousMatch, closeSearch]);

  return {
    // State
    isSearchOpen,
    searchQuery,
    replaceQuery,
    showReplace,
    currentMatchIndex,
    matches,
    matchingWordIds,
    currentMatch,
    
    // Options
    matchCase,
    useRegex,
    wholeWord,
    // Actions
    openSearch,
    closeSearch,
    setSearchQuery,
    setReplaceQuery,
    goToNextMatch,
    goToPreviousMatch,
    goToMatch,
    replaceCurrentMatch,
    replaceAllMatches,
    
    // Option toggles
    toggleMatchCase,
    toggleUseRegex,
    toggleWholeWord,
    toggleShowReplace,
    
    // Event handlers
    handleKeyDown
  };
};

export default useSearch;
